<template>
  <div class="asset-inventory-in-stock-container">
    <ds-page-header :title="$t('assetInventoryInStock.page.title')" :subtitle="$t('assetInventoryInStock.page.subtitle')">
      <template #extra>
        <el-button type="primary" size="small" icon="el-icon-printer" @click="_printAssetInventoryInStock">
          {{ $t('assetInventoryInStock.print') }}
        </el-button>
        <el-button size="small" icon="el-icon-back" @click="_goBack">{{ $t('assetInventoryInStock.back') }}</el-button>
      </template>
    </ds-page-header>

    <div class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('assetInventoryInStock.title') }}</span>
        <ds-status-tag size="sm" type="muted" :label="assetInventoryInStockInfo.stateName || '-'" />
      </div>
      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryInStock.inventoryName') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryInStockInfo.name || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryInStock.inventoryPerson') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryInStockInfo.staffName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryInStock.warehouse') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryInStockInfo.shName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryInStock.inventoryDate') }}</span>
          <span class="ds-info-grid__value ds-num">{{ assetInventoryInStockInfo.invTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryInStock.status') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryInStockInfo.stateName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item ds-info-grid__item--wide">
          <span class="ds-info-grid__label">{{ $t('assetInventoryInStock.remark') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryInStockInfo.remark || '-' }}</span>
        </div>
      </div>
    </div>

    <div class="ds-panel">
      <ds-toolbar :show-column-setting="false" :show-density="false" :show-refresh="false">
        <template #left><span class="ds-panel__title">{{ $t('assetInventoryInStock.inventoryItems') }}</span></template>
      </ds-toolbar>
      <ds-data-table :data="assetInventoryInStockInfo.storehouses" border
        :empty-description="$t('assetInventoryInStock.page.empty')">
        <el-table-column prop="shName" :label="$t('assetInventoryInStock.warehouseName')" align="center" min-width="110" />
        <el-table-column :label="$t('assetInventoryInStock.itemType')" align="center" min-width="140">
          <template slot-scope="scope">{{ scope.row.parentRstName || '-' }} &gt; {{ scope.row.rstName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="resName" :label="$t('assetInventoryInStock.itemName')" align="center" min-width="140" />
        <el-table-column :label="$t('assetInventoryInStock.specification')" align="center" width="110">
          <template slot-scope="scope">{{ scope.row.specName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="resCode" :label="$t('assetInventoryInStock.itemCode')" align="center" width="120">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.resCode || '-' }}</span></template>
        </el-table-column>
        <el-table-column prop="isFixedName" :label="$t('assetInventoryInStock.isFixedItem')" align="center" width="110" />
        <el-table-column prop="timesPrice" :label="$t('assetInventoryInStock.batchPrice')" align="center" width="110">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.timesPrice || '-' }}</span></template>
        </el-table-column>
        <el-table-column :label="$t('assetInventoryInStock.originalStock')" align="center" width="130">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.originalStock }}{{ scope.row.unitCodeName }}</span></template>
        </el-table-column>
        <el-table-column :label="$t('assetInventoryInStock.inventoryQuantity')" align="center" width="130">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.quantity }}{{ scope.row.unitCodeName }}</span></template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('assetInventoryInStock.remark')" align="center" min-width="140" />
      </ds-data-table>
    </div>

    <div v-if="assetInventoryInStockInfo.state === '3000' || assetInventoryInStockInfo.state === '4000'"
      class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('assetInventoryInStock.auditInfo') }}</span>
      </div>
      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryInStock.auditor') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryInStockInfo.auditName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryInStock.contactNumber') }}</span>
          <span class="ds-info-grid__value ds-num">{{ assetInventoryInStockInfo.auditTel || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryInStock.auditTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ assetInventoryInStockInfo.auditTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryInStock.auditOpinion') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryInStockInfo.opinion || '-' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { getAssetInventoryList, listAssetInventoryWholeDetail } from '@/api/resource/assetInventoryInStockApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'AssetInventoryInStockList',
  components: { DsPageHeader, DsToolbar, DsDataTable, DsStatusTag },
  data() {
    return {
      assetInventoryInStockInfo: {
        aiId: '', shId: '', shName: '', name: '', staffName: '', invTime: '',
        storehouses: [], remark: '', stateName: '', opinion: '', auditId: '',
        auditName: '', auditTel: '', auditTime: '', state: '', communityId: ''
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.assetInventoryInStockInfo.aiId = this.$route.query.aiId
    this._queryAssetInventory()
    this.queryAssetInventoryProduct()
  },
  methods: {
    _goBack() {
      this.$router.go(-1)
    },
    async _queryAssetInventory() {
      try {
        const params = {
          page: 1,
          row: 1,
          aiId: this.assetInventoryInStockInfo.aiId,
          communityId: this.communityId
        }
        const { data } = await getAssetInventoryList(params)
        if (data && data.length > 0) {
          Object.assign(this.assetInventoryInStockInfo, data[0])
        }
      } catch (error) {
        console.error('Failed to fetch asset inventory:', error)
      }
    },
    async queryAssetInventoryProduct() {
      try {
        const params = {
          page: 1,
          row: 100,
          applyOrderId: this.assetInventoryInStockInfo.aiId,
          communityId: this.communityId
        }
        const { data } = await listAssetInventoryWholeDetail(params)
        data.forEach(item => this._computeData(item))
        this.assetInventoryInStockInfo.storehouses = data
      } catch (error) {
        console.error('Failed to fetch inventory products:', error)
      }
    },
    _computeData(item) {
      item.invProfit = parseInt(item.invQuantity) - parseInt(item.quantity)
      if (item.invProfit < 0) item.invProfit = 0
      item.invLoss = parseInt(item.quantity) - parseInt(item.invQuantity)
      if (item.invLoss < 0) item.invLoss = 0
      item.invProfitMoney = item.invProfit * parseFloat(item.price)
      item.invLossMoney = item.invLoss * parseFloat(item.price)
    },
    _printAssetInventoryInStock() {
      window.open(`/#/pages/property/printAssetInventoryInStock?aiId=${this.assetInventoryInStockInfo.aiId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.asset-inventory-in-stock-container { padding: var(--ds-spacing-lg); background-color: var(--ds-gray-100); min-height: calc(100vh - 60px); }
.ds-panel { background: #fff; border: 1px solid var(--ds-gray-300); border-radius: var(--ds-radius-md); box-shadow: var(--ds-shadow-sm); overflow: hidden; margin-bottom: var(--ds-spacing-lg);
  &--padded { padding: var(--ds-spacing-lg); }
  &__header { display: flex; align-items: center; justify-content: space-between; padding-bottom: var(--ds-spacing-md); margin-bottom: var(--ds-spacing-md); border-bottom: 1px solid var(--ds-gray-200); }
  &__title { font-size: var(--ds-font-size-lg); font-weight: 600; color: var(--ds-gray-800); } }
.ds-info-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: var(--ds-spacing-md) var(--ds-spacing-lg);
  &__item { display: flex; flex-direction: column; gap: 4px; &--wide { grid-column: 1 / -1; } }
  &__label { font-size: var(--ds-font-size-sm); color: var(--ds-gray-500); }
  &__value { font-size: var(--ds-font-size-md); color: var(--ds-gray-800); word-break: break-all; } }
</style>
